import { lengthLabel, type LengthUnit } from "@CommonUtils/cdtStudio/domain/studioTypes";
// `report.pins` is never reported: seqOrder is as-built, so every order defect would produce it.
import type { InferenceReport } from "./cdtInfer";
import { categoryLengthUnitOf } from "../domain/studioLengthUnit";

const RESOLVER_SIZE_ATTR = "__sizeLabel";
const STUDIO_SIZE_ATTR = "size";

const studioAttr = (attr: string, quantityAttribute: string): string =>
  attr === RESOLVER_SIZE_ATTR ? quantityAttribute : attr;

export type InferenceExceptionKind =
  | "orient-disagrees"
  | "no-order"
  | "contrary-block"
  | "order-drift"
  | "gradient-section"
  | "size-unparsed"
  | "unit-mix"
  | "pin-backwards";

export const ALERT_KINDS: ReadonlySet<InferenceExceptionKind> = new Set<InferenceExceptionKind>([
  "size-unparsed",
  "unit-mix",
  "gradient-section",
]);

export interface InferenceException {
  id: string;
  kind: InferenceExceptionKind;
  title: string;
  detail: string;
  attr: string | null;
  scopeKey: string | null;
  skuIds: readonly string[];
}

export interface LevelConfidence {
  attr: string;
  label: string;
  best: number | null;
  vorder: number | null;
  pins: number | null;
  magnitude: number | null;
  convention: boolean;
}

export interface InferenceSummary {
  confidenceByAttr: Readonly<Record<string, LevelConfidence>>;
  exceptions: readonly InferenceException[];
}

const pct = (x: number | null): string => (x == null ? "—" : `${Math.round(100 * x)}%`);

export type ContraryBlockJudge = (
  depth: number,
  blockKey: string,
  declared: "horizontal" | "vertical",
) => "keep" | "drop";

/** Pair tally cannot tell a wrapped run from true shelf bands. */
export type LevelOrientationJudge = (
  depth: number,
  attr: string,
  declared: "horizontal" | "vertical",
) => "keep" | "drop";

export interface InferenceJudges {
  contrary?: ContraryBlockJudge;
  level?: LevelOrientationJudge;
  /** Must match `StudioTreeCanvas`. */
  bandedDepth?: number;
  skuIdsForAttrValues?: (attr: string, values: readonly string[]) => readonly string[];
  quantityAttribute?: string;
  /** The canvas shelf -> the row rank a merchant reads, when the canvas skips empty hang lines. */
  rankOfShelf?: (shelf: number) => number;
  /** The category's unit, for the exceptions' words only. */
  lengthUnit?: LengthUnit;
}

export const summarizeInference = (
  report: InferenceReport,
  judges: InferenceJudges | ContraryBlockJudge = {},
): InferenceSummary => {
  const {
    contrary: contraryJudge,
    level: levelJudge,
    bandedDepth = -1,
    quantityAttribute = STUDIO_SIZE_ATTR,
    skuIdsForAttrValues,
    rankOfShelf = (shelf: number) => shelf,
    lengthUnit = categoryLengthUnitOf(),
  } = typeof judges === "function"
    ? {
        contrary: judges,
        level: undefined,
        bandedDepth: -1,
        quantityAttribute: STUDIO_SIZE_ATTR,
        skuIdsForAttrValues: undefined,
      }
    : judges;
  const toStudioAttr = (attr: string) => studioAttr(attr, quantityAttribute);
  const confidenceByAttr: Record<string, LevelConfidence> = {};
  for (const candidate of report.candidates) {
    const best = Math.max(candidate.vorder ?? 0, candidate.pins ?? 0, candidate.magnitude ?? 0);
    const measurable =
      candidate.vorder != null || candidate.pins != null || candidate.magnitude != null;
    confidenceByAttr[toStudioAttr(candidate.attr)] = {
      attr: toStudioAttr(candidate.attr),
      label: candidate.label,
      best: measurable ? best : null,
      vorder: candidate.vorder,
      pins: candidate.pins,
      magnitude: candidate.magnitude,
      convention: measurable && best < 0.6,
    };
  }
  const quantityLabel = confidenceByAttr[quantityAttribute]?.label ?? "Quantity";

  // Engine "horizontal" is vertical blocking only above the first banded level.
  const merch = (depth: number, orientation: string): string => {
    if (orientation === "vertical") return "horizontal blocks (each on its own shelves)";
    return bandedDepth >= 0 && depth < bandedDepth
      ? "vertical blocks (each a full-height column)"
      : "side-by-side blocks (each a run along the shelf)";
  };
  const merchShort = (depth: number, orientation: string): string => {
    if (orientation === "vertical") return "horizontal blocks";
    return bandedDepth >= 0 && depth < bandedDepth ? "vertical blocks" : "side-by-side blocks";
  };
  const opposite = (orientation: string): string =>
    orientation === "horizontal" ? "vertical" : "horizontal";

  const exceptions: InferenceException[] = [];

  for (const level of report.levels.filter((entry) => !entry.agrees)) {
    if (levelJudge && levelJudge(level.depth, level.attr, level.declared) === "drop") continue;
    exceptions.push({
      id: `orient:${level.attr}`,
      kind: "orient-disagrees",
      title: `${level.label} blocked differently than proposed`,
      detail:
        `Proposed: ${merch(level.depth, level.declared)}. On the shelf, ` +
        `${pct(level.xOnly / (level.pairs || 1))} of ${level.pairs} pair(s) sit side by side, ` +
        `${pct(level.yOnly / (level.pairs || 1))} on separate shelves. Pick the blocking to use.`,
      attr: toStudioAttr(level.attr),
      scopeKey: null,
      skuIds: [],
    });
  }

  for (const candidate of Object.values(confidenceByAttr).filter((entry) => entry.convention)) {
    exceptions.push({
      id: `no-order:${candidate.attr}`,
      kind: "no-order",
      title: `Confirm ${candidate.label} order`,
      detail:
        `The current layout shows no clear ${candidate.label} flow (best fit ` +
        `${pct(candidate.best)} of pairs). Pick the flow to use.`,
      attr: candidate.attr,
      scopeKey: null,
      skuIds: [],
    });
  }

  for (const level of report.levels) {
    for (const block of level.contrary) {
      if (contraryJudge && contraryJudge(level.depth, block.key, level.declared) === "drop") {
        continue;
      }
      exceptions.push({
        id: `contrary:${level.attr}:${block.key}`,
        kind: "contrary-block",
        title: `${block.key || "This plan"} blocked differently`,
        detail: `Here ${level.label} ${
          level.declared === "horizontal"
            ? `sits on separate shelves in ${block.y} pair(s), side by side in ${block.x}`
            : `sits side by side in ${block.x} pair(s), on separate shelves in ${block.y}`
        }. Elsewhere it uses ${merchShort(level.depth, level.declared)}. Keep it different?`,
        attr: toStudioAttr(level.attr),
        scopeKey: block.key || null,
        skuIds: [],
      });
    }
  }

  for (const order of report.order.filter(
    (entry) => entry.disc > 0 && entry.agreement != null && entry.agreement < 0.9,
  )) {
    const pairValues = new Set<string>();
    const sentences = order.worst.map(({ pair, n }) => {
      const [wantedFirst, wantedSecond] = pair.split("  ·before·  ");
      pairValues.add(wantedFirst);
      pairValues.add(wantedSecond);
      return `${wantedFirst} → ${wantedSecond} expected, ${n} pair(s) reversed`;
    });
    const comparisons = order.conc + order.disc;
    exceptions.push({
      id: `order-drift:${order.attr}`,
      kind: "order-drift",
      title: `${order.label} partly out of flow`,
      detail: sentences.join("; ") + `. ${order.disc} of ${comparisons} pair(s) out of flow.`,
      attr: toStudioAttr(order.attr),
      scopeKey: null,
      skuIds: skuIdsForAttrValues ? [...skuIdsForAttrValues(order.attr, [...pairValues])] : [],
    });
  }

  for (const section of report.gradient.sections.filter((entry) => entry.bad > 0)) {
    exceptions.push({
      id: `gradient:${section.sec}`,
      kind: "gradient-section",
      title: `${section.sec}: tall packs not always low`,
      detail:
        "Median pack height, top shelf down: " +
        section.rows
          .map((row) => `shelf ${rankOfShelf(row.shelf)} ${lengthLabel(row.h, lengthUnit)}`)
          .join(", ") +
        `. ${section.bad} of ${section.ok + section.bad} step(s) get shorter going down. ` +
        "Put taller packs lower?",
      attr: null,
      scopeKey: section.sec,
      skuIds: [],
    });
  }

  if (report.sizes.unparsed.length > 0) {
    // One alert, not one row per SKU. Counted as products: one product can carry several rows.
    const unparsedProducts = [
      ...new Set(report.sizes.unparsed.map((sku) => sku.id.split("::")[0])),
    ];
    exceptions.push({
      id: "size-unparsed",
      kind: "size-unparsed",
      title: `Pack size unreadable on ${unparsedProducts.length} product(s)`,
      detail:
        `Can't read the ${quantityLabel.toLowerCase()} for ` +
        report.sizes.unparsed
          .map((sku) => `${sku.id.split("::")[0]} ("${sku.label}", ${sku.w}×${sku.h})`)
          .join(", ") +
        ". Size order is estimated from pack dimensions; fix the pack-size data.",
      attr: quantityAttribute,
      scopeKey: null,
      skuIds: unparsedProducts,
    });
  }

  for (const block of report.sizes.mixedBlocks) {
    exceptions.push({
      id: `unit-mix:${block.node}`,
      kind: "unit-mix",
      title: `${block.node}: mixed pack-size units`,
      detail:
        "This group mixes " +
        Object.entries(block.families)
          .map(([family, count]) => `${count} ${family}`)
          .join(", ") +
        ". Default order: count, loads, weight, then volume. Check it fits.",
      attr: quantityAttribute,
      scopeKey: block.node,
      skuIds: [],
    });
  }

  return { confidenceByAttr, exceptions };
};
